import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MemberType, Squad, Team, UserStatus } from 'api/models';
import { AssigneeValue } from 'api/tasks';
import { SquadValue } from 'components/molecules/squad-picker';
import { SquadPayload } from 'components/molecules/squad-picker/types';
import { squadPayloadToSquad } from 'components/molecules/squad-picker/utils';
import { useTemplateTaskCommonActions } from 'components/organisms/templates/projects/plan/commons';
import { RootState } from 'data';
import { useTemplateMentionsData } from 'data/templates/hooks';
import { saveTemplatePlaceholder } from 'data/templates/projects';
import { getActiveTemplatePlaceholders } from 'data/templates/projects/selectors';
import { ProjectTemplateTask } from '../types';

const getResponsibleOn = (squad: Squad) => [
  ...new Set([
    ...squad?.teams?.map((team) => team.teamType),
    ...squad?.users?.map((user) => user?.userType as unknown as MemberType),
  ]),
];

export const useAssigneeChangeForTemplate = (
  task: ProjectTemplateTask,
): {
  onAssigneeChange: (selected: SquadPayload) => Squad;
  onPlaceholderCreate: (team: Team) => void;
  onUpdateAssignee: (assignee?: SquadValue) => void;
  assignee?: AssigneeValue;
  responsibleTeam: MemberType[];
} => {
  const { byId: teamsById } = useSelector(({ teams }: RootState) => teams);
  const { members } = useTemplateMentionsData();
  const templatePlaceholders = useSelector(getActiveTemplatePlaceholders());
  const { onUpdateAssignee, onChangeResponsibility } =
    useTemplateTaskCommonActions(task);
  const dispatch = useDispatch();
  const [assignee, setAssignee] = useState<AssigneeValue | undefined>();

  const placeholders = useMemo(
    () => ({
      ...teamsById,
      ...templatePlaceholders,
    }),
    [teamsById, templatePlaceholders],
  );
  const onAssigneeChange = useCallback(
    (selected: SquadPayload): Squad => {
      const newAssignee = squadPayloadToSquad(selected, {
        ...members.items,
        teams: Object.values(placeholders),
      });
      const responsibleOn = getResponsibleOn(newAssignee);

      onChangeResponsibility(responsibleOn);
      onUpdateAssignee(newAssignee);

      return newAssignee;
    },
    [members.items, onChangeResponsibility, onUpdateAssignee, placeholders],
  );
  const onPlaceholderCreate = useCallback(
    (team: Team) => {
      const newAssignee = squadPayloadToSquad(
        {
          users: [
            ...(assignee?.users || []).map((_user) => ({
              userId: _user.userId,
            })),
          ],
          teams: [
            ...(assignee?.teams || []).map((_team) => ({
              teamId: _team.teamId,
            })),
            { teamId: team.teamId },
          ],
        },
        {
          ...members.items,
          teams: [...Object.values(placeholders), team] as Team[],
        },
      );

      onUpdateAssignee(newAssignee);
      onChangeResponsibility(getResponsibleOn(newAssignee));
      dispatch(saveTemplatePlaceholder(team));
    },
    [
      assignee?.users,
      assignee?.teams,
      members.items,
      placeholders,
      onUpdateAssignee,
      onChangeResponsibility,
      dispatch,
    ],
  );

  useEffect(() => {
    if (!task.assignee) {
      setAssignee(undefined);

      return;
    }
    const { users, teams } = task.assignee;
    const activeUserIds = members.items.users
      .filter(({ userStatus }) => userStatus !== UserStatus.Inactive)
      .map(({ userId }) => userId);

    setAssignee({
      users: users?.filter(({ userId }) => activeUserIds.includes(userId)),
      teams: teams?.filter(({ teamId }) => !!placeholders[teamId]),
    });
  }, [task.assignee, placeholders, members.items.users]);

  const responsibleTeam = task.private
    ? [MemberType.Native]
    : [MemberType.External, MemberType.Native];

  return {
    onAssigneeChange,
    onPlaceholderCreate,
    onUpdateAssignee,
    assignee,
    responsibleTeam,
  };
};
